import { Anchor, Breadcrumbs, Group, SegmentedControl, Stack, Text } from "@mantine/core";
import { IconChevronRight, IconLayoutGrid } from "@tabler/icons-react";
import { useState } from "react";
import MetadataDocuments from "../MetadataDocuments";
import { ExpandGraph } from "./ExpandGraph";
import {
    type BucketSelection,
    DocumentViewMode,
    type DocumentViewsProps,
    type OverlayState,
} from "./types";

const VIEW_OPTIONS = [
    { value: DocumentViewMode.TABLE, label: "Table" },
    { value: DocumentViewMode.EXPLODE, label: "Overview" },
];

export const DocumentViews = ({
    index,
    rootLabel,
    fields,
    onFilteredCountChange,
}: DocumentViewsProps) => {
    const [mode, setMode] = useState<DocumentViewMode>(DocumentViewMode.TABLE);
    const [initialFilter, setInitialFilter] = useState<BucketSelection[]>();
    const [tableKey, setTableKey] = useState(0);
    const [category, setCategory] = useState("");
    const [selected, setSelected] = useState<BucketSelection | undefined>(undefined);
    const [overlay, setOverlay] = useState<OverlayState | undefined>(undefined);
    const [gridPage, setGridPage] = useState(0);

    const selectCategory = (field: string) => {
        setCategory(field);
        setSelected(undefined);
        setOverlay(undefined);
        setGridPage(0);
    };

    const clearCategory = () => {
        setCategory("");
        setSelected(undefined);
        setOverlay(undefined);
        setGridPage(0);
    };

    const openFiltered = (selection: BucketSelection[]) => {
        setOverlay(undefined);
        setInitialFilter(selection);
        setTableKey((current) => current + 1);
        setMode(DocumentViewMode.TABLE);
    };

    const selectValue = (selection: BucketSelection) => {
        setOverlay(undefined);
        setSelected((current) =>
            current?.field === selection.field && current?.value === selection.value
                ? undefined
                : selection,
        );
    };

    const clearSelection = () => {
        setOverlay(undefined);
        setSelected(undefined);
    };

    const isOverview = mode === DocumentViewMode.EXPLODE;
    const categoryLabel = fields.find((field) => field.value === category)?.label ?? category;

    const crumbs: { label: string; go?: () => void }[] = [{ label: rootLabel, go: clearCategory }];
    if (category) crumbs.push({ label: categoryLabel, go: () => selectCategory(category) });
    if (selected) crumbs.push({ label: selected.value, go: () => setOverlay(undefined) });
    if (overlay) crumbs.push({ label: overlay.label });

    return (
        <Stack gap="md">
            <Group gap="lg" align="center">
                <SegmentedControl
                    data={VIEW_OPTIONS}
                    value={mode}
                    onChange={(value) => {
                        const nextMode = value as DocumentViewMode;
                        setMode(nextMode);
                        if (nextMode === DocumentViewMode.EXPLODE) {
                            setInitialFilter(undefined);
                            onFilteredCountChange?.(undefined);
                        }
                    }}
                    w="fit-content"
                />
            </Group>

            {isOverview && (
                <Group
                    gap={0}
                    px="sm"
                    py={7}
                    w="fit-content"
                    maw="100%"
                    style={{
                        background: "#f3eeff",
                        border: "1px solid #e2d5fb",
                        borderRadius: 10,
                        overflowX: "auto",
                    }}
                >
                    <Breadcrumbs
                        separator={<IconChevronRight size={15} style={{ color: "#b9abe6" }} />}
                        separatorMargin={6}
                        styles={{ root: { flexWrap: "nowrap" } }}
                    >
                        {crumbs.map((crumb, index) => {
                            const isLast = index === crumbs.length - 1;
                            const inner = (
                                <span
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 5,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {index === 0 && <IconLayoutGrid size={15} />}
                                    {crumb.label}
                                </span>
                            );
                            if (isLast || !crumb.go)
                                return (
                                    <Text
                                        key={`${index}-${crumb.label}`}
                                        span
                                        size="sm"
                                        fw={700}
                                        c="white"
                                        style={{
                                            background: "#6d28d9",
                                            padding: "3px 12px",
                                            borderRadius: 20,
                                        }}
                                    >
                                        {inner}
                                    </Text>
                                );
                            return (
                                <Anchor
                                    key={`${index}-${crumb.label}`}
                                    size="sm"
                                    fw={600}
                                    c="violet.7"
                                    underline="hover"
                                    onClick={crumb.go}
                                >
                                    {inner}
                                </Anchor>
                            );
                        })}
                    </Breadcrumbs>
                </Group>
            )}

            {isOverview ? (
                <ExpandGraph
                    index={index}
                    rootLabel={rootLabel}
                    category={category}
                    categoryLabel={categoryLabel}
                    fields={fields}
                    selected={selected}
                    overlay={overlay}
                    gridPage={gridPage}
                    onGridPage={setGridPage}
                    onSelect={selectValue}
                    onClearSelection={clearSelection}
                    onSelectCategory={selectCategory}
                    onClearCategory={clearCategory}
                    onOpen={openFiltered}
                    onOpenOverlay={setOverlay}
                    onCloseOverlay={() => setOverlay(undefined)}
                />
            ) : (
                <MetadataDocuments
                    key={`${index}-${tableKey}`}
                    index={index}
                    initialFilter={initialFilter}
                    onFilteredCountChange={onFilteredCountChange}
                />
            )}
        </Stack>
    );
};
